/**
 * Ultimate AI - "Zenith"
 *
 * Hybrid approach combining:
 * - Pattern recognition for quick tactical wins
 * - Minimax for deep tactical analysis
 * - MCTS for complex position evaluation
 *
 * Adapts its strategy based on position type.
 *
 * Strengths:
 * - Best of all approaches
 * - Adapts to position complexity
 * - Strong in all game phases
 *
 * Weaknesses:
 * - More complex implementation
 * - Requires careful time management
 */

import { GameState, IMove, Position, PieceType } from "../../types";
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from "../Core/IAIPlayer";
import { ZodiacEngine } from "../../zodiacEngine";
import { HybridEngine } from "./HybridEngine";

export class UltimateAI implements IAIPlayer {
  readonly name = AIPlayerType.ULTIMATE;
  readonly displayName = "Zenith";
  readonly description = "Hybrid AI combining pattern recognition, minimax, and MCTS";

  private hybridEngine: HybridEngine;
  private gameEngine: ZodiacEngine;
  private stats: AIStats = {
    movesCalculated: 0,
    averageThinkingTime: 0,
    nodesEvaluated: 0,
  };
  private totalThinkingTime = 0;

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.hybridEngine = new HybridEngine(gameEngine);
  }

  /**
   * Zenith AI King Selection - Strategic piece selection based on mobility, defensive capability, and race potential
   *
   * Selection Philosophy:
   * - Mobility: Prefer pieces that can move flexibly (Crane, Monkey, Frog)
   * - Defense: Balance offense with survivability (avoid ultra-fragile kings)
   * - Race Potential: Consider pieces that can reach opponent's home row quickly
   * - Versatility: Prefer pieces with special abilities that create tactical opportunities
   */
  selectKing(availableKings: PieceType[], isLightSide: boolean): PieceType {
    // Define strategic scoring for each piece type
    const pieceScores: Record<string, number> = {
      // Top tier - High mobility, strong abilities
      [PieceType.Crane]: 90,     // Excellent mobility (flies), hard to block, great racer
      [PieceType.Monkey]: 85,    // Swing ability for quick repositioning, aggressive potential
      [PieceType.Frog]: 82,      // Lily jump opener, flexible movement, solid racer

      // Mid tier - Good balance
      [PieceType.Snake]: 75,     // Respawn ability provides insurance, decent mobility
      [PieceType.Wolf]: 72,      // Decent mobility, no special weakness
      [PieceType.Rhino]: 70,     // Immune to many pieces, but linear movement

      // Lower tier - Defensive or situational
      [PieceType.Tree]: 65,      // Grows stronger (fungi immunity), but slow early game
      [PieceType.Goat]: 60,      // Faint ability is defensive, decent mobility
      [PieceType.Bear]: 55,      // Immune to monkey, but slow and predictable

      // Avoid tier - Too defensive or niche
      [PieceType.Fungi]: 45,     // Cannot be captured by Tree, but very weak mobility as king
      [PieceType.Virus]: 40,     // Infect ability doesn't help king survival, slow
      [PieceType.Octopus]: 35,   // Entangle is great for support, terrible for king (slow, restricted)
    };

    // Calculate scores for available kings
    const scoredKings = availableKings.map(king => ({
      piece: king,
      score: pieceScores[king] || 50, // Default 50 if piece not in scoring table
    }));

    // Sort by score descending
    scoredKings.sort((a, b) => b.score - a.score);

    // Pick best king (top score)
    const selectedKing = scoredKings[0].piece;


    return selectedKing;
  }

  /**
   * Get reasoning for king selection (for logging/debugging)
   */
  private getKingSelectionReasoning(king: PieceType): string {
    const reasoning: Record<string, string> = {
      [PieceType.Crane]: "Excellent mobility (flies), hard to block, great race potential",
      [PieceType.Monkey]: "Swing ability for repositioning, aggressive potential",
      [PieceType.Frog]: "Lily jump gives early flexibility, solid racer",
      [PieceType.Snake]: "Respawn provides safety insurance, decent mobility",
      [PieceType.Wolf]: "Good all-rounder, no significant weaknesses",
      [PieceType.Rhino]: "Immune to many pieces, strong defensive choice",
      [PieceType.Tree]: "Grows stronger over time (fungi immune), patient strategy",
      [PieceType.Goat]: "Faint ability provides emergency escape, flexible movement",
      [PieceType.Bear]: "Immune to Monkey, tanky but predictable",
      [PieceType.Fungi]: "Risky pick - weak mobility compensated by Tree immunity",
      [PieceType.Virus]: "Unconventional choice - infect doesn't help king survival",
      [PieceType.Octopus]: "Not recommended - slow king, entangle better as support piece",
    };
    return reasoning[king] || "Strategic selection based on mobility and versatility";
  }

  async selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult> {
    const startTime = Date.now();

    // Run hybrid search
    const result = this.hybridEngine.findBestMove(gameState, maxThinkingTime);

    if (!result) {
      throw new Error("Zenith AI failed to find a valid move");
    }

    const thinkingTime = Date.now() - startTime;

    // Update stats
    this.stats.movesCalculated++;
    this.totalThinkingTime += thinkingTime;
    this.stats.averageThinkingTime = this.totalThinkingTime / this.stats.movesCalculated;

    return {
      fromPosition: result.fromPosition,
      move: result.move,
      confidence: result.confidence,
      thinkingTime,
      evaluation: result.score,
      reasoning: `${this.getMethodName(result.method)}: ${result.details}`,
    };
  }

  getStats(): AIStats {
    return { ...this.stats };
  }

  reset(): void {
    this.stats = {
      movesCalculated: 0,
      averageThinkingTime: 0,
      nodesEvaluated: 0,
    };
    this.totalThinkingTime = 0;
  }

  configure(config: Record<string, any>): void {
    // Could expose configuration for sub-engines
  }

  /**
   * Get friendly name for method
   */
  private getMethodName(method: string): string {
    switch (method) {
      case "pattern":
        return "Pattern Recognition";
      case "tactical_minimax":
        return "Tactical Analysis";
      case "mcts":
        return "Monte Carlo Search";
      case "hybrid":
        return "Hybrid Consensus";
      default:
        return "Unknown Method";
    }
  }
}

export { HybridEngine } from "./HybridEngine";
export { PatternLibrary } from "./PatternLibrary";